import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MouseGlow } from '@/components/site/MouseGlow';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { CTABand } from '@/components/site/CTABand';
import { DuotoneImage } from '@/components/site/DuotoneImage';
import { WORK } from '@/data/site';
import { WORK_IMAGES } from '@/data/homeImages';

gsap.registerPlugin(ScrollTrigger);

export default function Work() {
  const listRef = useRef(null);

  // signature animation: scroll-linked horizontal drift on year column
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !listRef.current) return;
    const ctx = gsap.context(() => {
      gsap.utils.toArray('.work-row').forEach((row, i) => {
        gsap.fromTo(
          row.querySelector('.work-year'),
          { xPercent: 30, opacity: 0 },
          {
            xPercent: 0,
            opacity: 1,
            ease: 'power2.out',
            scrollTrigger: { trigger: row, start: 'top 80%', end: 'top 50%', scrub: 0.6 },
          }
        );
        gsap.fromTo(
          row.querySelector('.work-stat'),
          { yPercent: 40, opacity: 0 },
          {
            yPercent: 0,
            opacity: 1,
            ease: 'power2.out',
            scrollTrigger: { trigger: row, start: 'top 80%', end: 'top 50%', scrub: 0.6 },
          }
        );
      });
    }, listRef);
    return () => ctx.revert();
  }, []);

  return (
    <div data-testid="work-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <MouseGlow intensity={0.28} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 md:pt-40 pb-16 md:pb-24">
          <SectionLabel as="h1" num="01" data-testid="work-eyebrow">
            Our Work — Websites, Apps &amp; Software We Built
          </SectionLabel>
          <WordReveal
            as="p"
            text="Five live products. Open any of them."
            accent="live"
            data-testid="work-headline"
            className="mt-8 text-4xl sm:text-5xl md:text-6xl lg:text-[80px] font-extrabold tracking-tightest leading-[1.02] text-balance max-w-5xl"
          />
          <Reveal delay={500}>
            <p className="mt-10 text-lg md:text-xl text-ink-muted max-w-2xl leading-relaxed">
              Every project here is live and linked. Two are products we built and run ourselves,
              three are client builds. Each has a page with what we built and real screenshots.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LIST */}
      <section ref={listRef} data-testid="work-list" className="relative border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {WORK.map((w, i) => (
            <article
              key={w.slug}
              id={w.slug}
              data-testid={`work-${w.slug}`}
              className="work-row group scroll-mt-32 border-b border-white/10 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-start"
            >
              <div className="md:col-span-1 work-year">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted tabular-nums">{w.year}</div>
              </div>

              <div className="md:col-span-6">
                <div className="text-[11px] uppercase tracking-[0.28em] text-ink-muted mb-4">
                  {w.sector}
                </div>
                <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.04] text-white">
                  <Link to={`/work/${w.slug}/`} className="hover:text-violet transition-colors">{w.name}</Link>
                </h2>
                <p className="mt-5 text-ink-muted max-w-xl leading-relaxed">{w.summary}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {w.services.map((sv) => (
                    <span
                      key={sv}
                      className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-white/[0.12] text-white/85"
                    >
                      {sv}
                    </span>
                  ))}
                </div>
              </div>

              <div className="md:col-span-5 md:pl-8 md:border-l border-white/10">
                {/* Visual block */}
                <div
                  className="relative aspect-[5/3] md:aspect-[5/4] rounded-[20px] overflow-hidden border border-white/[0.12] transition-all duration-700 group-hover:border-white/25"
                  style={{
                    background: `radial-gradient(circle at 30% 20%, ${w.palette[0]}55, transparent 55%), radial-gradient(circle at 80% 80%, ${w.palette[1] || w.palette[0]}40, transparent 50%), #0E0E15`,
                  }}
                >
                  {WORK_IMAGES[w.slug] && (
                    <DuotoneImage src={WORK_IMAGES[w.slug].src} alt={WORK_IMAGES[w.slug].alt} duotone={0.15} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-bg/85 via-ink-bg/20 to-ink-bg/40" />
                  <div className="absolute inset-0 grid-bg opacity-20" />
                  <div className="absolute inset-0 flex flex-col p-6 justify-between">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">
                      Case index — {String(i + 1).padStart(2, '0')}
                    </div>
                    <div className="work-stat">
                      <div className="text-5xl md:text-6xl font-bold tracking-tight tabular-nums text-white">
                        {w.stat.kpi}
                      </div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-white/75">
                        {w.stat.label}
                      </div>
                    </div>
                  </div>
                </div>
                <Link
                  to={`/work/${w.slug}/`}
                  className="arrow-link mt-5 inline-flex items-center gap-2 text-sm text-white"
                  data-testid={`work-cta-${w.slug}`}
                >
                  Read the {w.name} case study <ArrowUpRight size={14} className="arrow" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      <CTABand
        eyebrow="Have something similar in mind?"
        headline="Tell us what you want to build."
        sub="A free 30-minute call on WhatsApp. We’ll walk you through how these were built and what yours would take."
      />
    </div>
  );
}
